import { Component, Input } from '@angular/core';

export type AccountIconName =
  | 'user' | 'grid' | 'file' | 'key' | 'shield' | 'devices' | 'lock' | 'mail' | 'phone' | 'pin'
  | 'globe' | 'camera' | 'users' | 'external' | 'download' | 'printer' | 'info' | 'headset'
  | 'settings' | 'logout' | 'mobile' | 'laptop' | 'check' | 'chevron' | 'clock' | 'message';

// 24x24 stroke-based icon paths (currentColor). Kept inline so no icon registration is needed.
const ICON_PATHS: Record<AccountIconName, string | string[]> = {
  user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z',
  grid: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z',
  file: 'M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8zM14 3v5h5M9 13h6M9 17h6',
  key: 'M15 3a6 6 0 0 0-5.7 7.9L3 17.2V21h3.8l1-1v-2h2v-2h2l1.3-1.3A6 6 0 1 0 15 3zm1.5 4.5a1 1 0 1 1 0 2 1 1 0 0 1 0-2z',
  shield: 'M12 3l7 3v5c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V6zM9 12l2 2 4-4',
  devices: 'M3 6h11v9H3zM8 15v3M5 18h6M16 9h5v9h-5zM18.5 16v.5',
  lock: 'M6 11h12v10H6zM9 11V7a3 3 0 0 1 6 0v4M12 15v2',
  mail: 'M3 6h18v12H3zM3 7l9 6 9-6',
  phone: 'M8 3h8v18H8zM11 18h2',
  pin: 'M12 21s-6-5.5-6-11a6 6 0 0 1 12 0c0 5.5-6 11-6 11zM12 12a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  globe: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18',
  camera: 'M4 8h4l2-2h4l2 2h4v11H4zM12 17a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
  users: 'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8',
  external: 'M14 4h6v6M20 4l-9 9M18 13v6H5V6h6',
  download: 'M12 3v12M6 10l6 6 6-6M4 20h16',
  printer: 'M7 8V3h10v5M5 8h14v8h-3v-3H8v3H5zM8 13h8v8H8z',
  info: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 11v6M12 7.5v.5',
  headset: 'M4 13a8 8 0 0 1 16 0M4 13v5h4v-6H4zM20 13v5h-4v-6h4zM20 18v1a2 2 0 0 1-2 2h-4',
  // Gear/cog (not the old sun/brightness strokes).
  settings: [
    'M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z',
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  ],
  logout: 'M10 4H4v16h6M14 8l4 4-4 4M18 12H9',
  mobile: 'M8 3h8v18H8zM11 18h2',
  laptop: 'M4 6h16v10H4zM2 19h20',
  check: 'M5 12l5 5 9-10',
  chevron: 'M9 6l6 6-6 6',
  clock: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 8v5l3 2',
  message: 'M4 5h16v11H9l-5 4z',
};

@Component({
  selector: 'app-account-icon',
  template: `<svg [attr.width]="size" [attr.height]="size" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path *ngFor="let d of paths" [attr.d]="d"></path>
  </svg>`,
  styles: [':host { display: inline-flex; line-height: 0; vertical-align: middle; }'],
  standalone: false,
})
export class AccountIconComponent {
  @Input() size = 18;
  paths: string[] = [];

  @Input() set name(value: AccountIconName) {
    const entry = ICON_PATHS[value];
    this.paths = !entry ? [] : Array.isArray(entry) ? entry : [entry];
  }
}
